import type React from 'react';
import styled from 'styled-components';

import Skeleton from 'app/ui/components/atoms/skeleton';
import {
  LibraryTrainingPlansHeader,
  type TitleVariant,
  TrainingPlansArea,
} from 'app/ui/components/molecules/library-training-plans';

// One tracker followed by the first few day cards, the same shapes ActiveTrainingPlanRail lays
// out, so the section keeps its footprint while the plans are still loading.
const SKELETON_CARD_COUNT = 3;
const skeletonCards = Array.from({ length: SKELETON_CARD_COUNT }, (_, index) => index);

// The tracker's own greys, so the placeholders sit on the dark section the way the real cards do.
const Placeholder = styled(Skeleton).attrs(({ theme }) => ({
  $tones: { base: theme.colors.faqGrey, highlight: theme.colors.veryGray },
}))`
  border-radius: ${({ theme }) => theme.spacing(2)};
`;

const SkeletonPlan = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(4)};
`;

// Stands in for the plan's h5 title above its rail.
const PlanTitleLine = styled(Placeholder)`
  width: 14rem;
  height: 1.25rem;
  border-radius: ${({ theme }) => theme.spacing(4)};
`;

const SkeletonRail = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(4)};
  width: 100%;
  overflow: hidden;
`;

// Mirrors StyledTracker in ActiveTrainingPlanRail: 20rem, then 25rem and 30rem up the breakpoints.
const TrackerPlaceholder = styled(Placeholder)`
  flex-shrink: 0;
  width: 20rem;
  min-height: 10.375rem;

  ${({ theme: { media } }) =>
    media.medium(
      () => `
    width: 25rem;
  `,
    )}

  ${({ theme: { media } }) =>
    media.large(
      () => `
    width: 30rem;
  `,
    )}
`;

// Mirrors SimplifiedClassCard's container: 20rem wide at the card's aspect ratio.
const CardPlaceholder = styled(Placeholder)`
  flex-shrink: 0;
  width: 20rem;
  aspect-ratio: calc(420 / 260);
`;

type LibraryTrainingPlansSkeletonProps = {
  titleVariant?: TitleVariant;
};

/**
 * What LibraryTrainingPlansSection shows while the active plans are loading: the real section
 * header (with the "See all" slot reserved, so the title row is final) over a shimmering stand-in
 * for one plan's rail.
 *
 * The stand-in is sized for the common outcome - a single active plan - and on desktop the
 * StartPlanBanner is within a few pixels of it too. It cannot know the outcome in advance, so a
 * second active plan, the mobile banner (190px) or the empty state will still move whatever sits
 * below the section once the plans land.
 */
const LibraryTrainingPlansSkeleton: React.FC<LibraryTrainingPlansSkeletonProps> = ({ titleVariant = 'h3' }) => (
  <TrainingPlansArea aria-busy="true" data-testid="library-training-plans-skeleton">
    <LibraryTrainingPlansHeader titleVariant={titleVariant} seeAll="reserved" />
    <SkeletonPlan aria-hidden="true">
      <PlanTitleLine />
      <SkeletonRail>
        <TrackerPlaceholder />
        {skeletonCards.map((card) => (
          <CardPlaceholder key={card} />
        ))}
      </SkeletonRail>
    </SkeletonPlan>
  </TrainingPlansArea>
);

export default LibraryTrainingPlansSkeleton;
